import React, { createContext, useContext, useState, useCallback } from 'react'

interface NewChatContextValue {
  /** Increments each time a new chat is requested; 0 means no request pending. */
  newChatRequestId: number
  /** Call from the sidebar to signal a new chat should be created. */
  requestNewChat: () => void
  /** Call from AskClaudePage after handling the request. */
  clearNewChatRequest: () => void
}

const NewChatContext = createContext<NewChatContextValue | null>(null)

/**
 * Provider that bridges the sidebar "New Chat" button with AskClaudePage's WebSocket.
 *
 * @returns JSX.Element The provider wrapping children
 */
export const NewChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [requestId, setRequestId] = useState(0)

  const requestNewChat = useCallback(() => {
    setRequestId((prev) => prev + 1)
  }, [])

  const clearNewChatRequest = useCallback(() => {
    setRequestId(0)
  }, [])

  return (
    <NewChatContext.Provider value={{ newChatRequestId: requestId, requestNewChat, clearNewChatRequest }}>
      {children}
    </NewChatContext.Provider>
  )
}

/**
 * Hook to access the new-chat signaling context.
 *
 * @returns NewChatContextValue
 */
export const useNewChatContext = (): NewChatContextValue => {
  const ctx = useContext(NewChatContext)
  if (!ctx) {
    throw new Error('useNewChatContext must be used within a NewChatProvider')
  }
  return ctx
}
